import { z } from 'zod';

/**
 * How a task repeats.
 * - `daily`: every `interval` days.
 * - `weekly`: on the given weekdays (0 = Sunday) every `interval` weeks.
 * - `monthly`: on `dayOfMonth` every `interval` months (clamped to the month's last day).
 * - `after_completion`: `interval` days after the task is completed.
 */
export const recurrenceRuleSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('daily'), interval: z.number().int().min(1).max(365).default(1) }),
  z.object({
    type: z.literal('weekly'),
    interval: z.number().int().min(1).max(52).default(1),
    days: z.array(z.number().int().min(0).max(6)).min(1).max(7),
  }),
  z.object({
    type: z.literal('monthly'),
    interval: z.number().int().min(1).max(24).default(1),
    dayOfMonth: z.number().int().min(1).max(31),
  }),
  z.object({ type: z.literal('after_completion'), interval: z.number().int().min(1).max(365) }),
]);
export type RecurrenceRule = z.infer<typeof recurrenceRuleSchema>;

export interface RecurrenceDTO {
  taskId: string;
  rule: RecurrenceRule;
  /** When the next copy of the task will be created (UTC ms), or null while waiting on completion. */
  nextRunAt: number | null;
}

const DAY = 86_400_000;

function utcDay(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/**
 * The next occurrence strictly after `after`, as a UTC midnight timestamp (the date-only convention
 * used for task dates). `anchor` is the date the series is counted from: the task's due date, or
 * its creation date. For `after_completion`, pass the completion time as `after`.
 */
export function nextOccurrence(rule: RecurrenceRule, after: number, anchor: number = after): number {
  const from = utcDay(after);
  const start = utcDay(anchor);
  switch (rule.type) {
    case 'after_completion':
      return from + rule.interval * DAY;
    case 'daily': {
      const step = rule.interval * DAY;
      if (from < start) return start;
      const elapsed = Math.floor((from - start) / step);
      return start + (elapsed + 1) * step;
    }
    case 'weekly': {
      const days = [...new Set(rule.days)].sort((a, b) => a - b);
      // Weeks are counted from the Sunday of the anchor's week.
      const anchorWeek = start - new Date(start).getUTCDay() * DAY;
      for (let day = from + DAY, guard = 0; guard < 7 * rule.interval * 2 + 14; day += DAY, guard++) {
        const weekIndex = Math.floor((day - anchorWeek) / (7 * DAY));
        if (weekIndex < 0 || weekIndex % rule.interval !== 0) continue;
        if (days.includes(new Date(day).getUTCDay())) return day;
      }
      return from + 7 * DAY;
    }
    case 'monthly': {
      const a = new Date(start);
      const f = new Date(from);
      const monthsApart = (f.getUTCFullYear() - a.getUTCFullYear()) * 12 + (f.getUTCMonth() - a.getUTCMonth());
      // First candidate month on the interval grid at or after the month of `from`.
      let offset = Math.max(0, Math.ceil(monthsApart / rule.interval) * rule.interval);
      for (let guard = 0; guard < 4; guard++, offset += rule.interval) {
        const year = a.getUTCFullYear() + Math.floor((a.getUTCMonth() + offset) / 12);
        const month = (a.getUTCMonth() + offset) % 12;
        const candidate = Date.UTC(year, month, Math.min(rule.dayOfMonth, daysInMonth(year, month)));
        if (candidate > from) return candidate;
      }
      return from + 30 * DAY;
    }
  }
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function ordinal(n: number): string {
  const rem = n % 100;
  if (rem >= 11 && rem <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

/** Human description, e.g. "Every 2 weeks on Monday and Thursday". */
export function describeRecurrence(rule: RecurrenceRule): string {
  const every = (unit: string) => (rule.interval === 1 ? `Every ${unit}` : `Every ${rule.interval} ${unit}s`);
  switch (rule.type) {
    case 'daily':
      return every('day');
    case 'weekly': {
      const names = [...new Set(rule.days)].sort((a, b) => a - b).map((d) => WEEKDAYS[d]!);
      const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names.at(-1)}` : names[0];
      return `${every('week')} on ${list}`;
    }
    case 'monthly':
      return `${every('month')} on the ${ordinal(rule.dayOfMonth)}`;
    case 'after_completion':
      return rule.interval === 1 ? '1 day after completion' : `${rule.interval} days after completion`;
  }
}
